import React, { FC, useEffect, useState } from 'react'
import { ModuleNames } from '../../../../constants/componentNames.constants'
import { MENU_STYLES } from '../../../../constants/useCases.constants'
import { MenuGroup } from '../../../../models/others/menuGroup'
import { TopicCTA } from '../../../../models/topics/cta'
import ImgUtil from '../../../../utils/img.utils'
import { internalLinkHandler, isExternalURL } from '../../../../utils/link.utils'
import formatInlineCode from '../../../../utils/shortTextFormatting'
import Button from '../../button'
import MainNavBanner from '../../mainNavBanner'
import Typography from '../../typography'
import WrapperImage from '../../wrapperImage'

import {
  MegaMenu,
  MegaMenuContainer,
  MegaMenuSection,
  NavLink,
  NavLinksWrapper,
  SubListItem,
} from '../style'

type NavLinkProps = {
  navLinks: (TopicCTA | MenuGroup)[]
  openMenu: boolean
  uiPathSrc: string
  location: string
}

type MegaMenuProps = {
  subLinks: (TopicCTA | MenuGroup)[]
  numOfGrayColumns: number
  direction: string
  uiPathSrc: string
  navLinkId: string
  navIndex: number
  menuStyle: string
  location: string
  parentBottomText: string
  parentBottomTextCta: TopicCTA
  hideMegamenuFirstColumn: boolean
  leftMarginColor: string
  rightMarginColor: string
  parentLink: any
  newGroupName: string
}

const AccordionTargetClass = {
  MAIN_ACCORDION: 'js-Menu-item',
  SUB_ACCORDION: 'Navbar__Sub_Accordion_Item',
}
const handleAccordionOpen = (event: any, targetClass: string) => {
  const current = event.currentTarget
  const parent = event.currentTarget.parentElement

  if (current.classList.contains('is-active')) {
    current.classList.remove('is-active')
    parent.classList.remove('NavLink__Active')
  } else {
    let menuItems = document.querySelectorAll('.' + targetClass)

    for (let item of menuItems) {
      if (item !== event.currentTarget) {
        const itemParent = item.parentElement
        itemParent?.classList.remove('NavLink__Active')
        item.classList.remove('is-active')
      }
    }

    parent.classList.add('NavLink__Active')
    current.classList.add('is-active')
  }

  let subAccord = current.nextElementSibling.querySelectorAll('.Navbar__Sub_Accordion')
  for (let item of subAccord) {
    item?.classList.remove('NavLink__Active')
    item.querySelector('.Navbar__Sub_Accordion_Item').classList.remove('is-active')
  }
}

const mainNavHoverOut = (event: any) => {
  const parentEle = document.querySelectorAll('.Navbar__MenuGroup')
  parentEle.forEach(pEle => {
    setTimeout(function () {
      pEle.classList.remove('open')
    }, 1000)
  })
}

export const NavLinks: FC<NavLinkProps> = ({ navLinks, openMenu, uiPathSrc, location }) => (
  <NavLinksWrapper $openMenu={openMenu}>
    {navLinks?.map(
      (
        {
          id,
          newGroupName,
          parentLink,
          pages,
          greyColumns,
          greyColumnAlignment,
          bottomText,
          bottomTextCta,
          menuStyle,
          hideMegamenuFirstColumn,
          leftMarginColor,
          rightMarginColor,
        }: any,
        index: number
      ) => {
        const internalLinkSrc =
          parentLink?.internalLink?.slug && uiPathSrc + parentLink.internalLink.slug
        const menuGroupSrc = parentLink?.externalUrl || internalLinkSrc || '#'
        const menuGroupTarget = isExternalURL(menuGroupSrc) ? '_blank' : '_self'

        const mainNavHover = (event: any) => {
          const parent = '.' + event.currentTarget.parentElement.className
          const parentEle = document.querySelectorAll(parent)
          parentEle.forEach(pEle => {
            pEle.classList.remove('open')
            let subMenuTab = pEle.querySelectorAll('.sub-mgrouptab')
            let subMenuTabContent = pEle.querySelectorAll('.Navbar__Sub_Accordion')
            if (subMenuTab.length || subMenuTabContent.length) {
              subMenuTab.forEach(item => {
                item.classList.remove('is-active')
              })
              subMenuTab[0].classList.add('is-active')

              subMenuTabContent.forEach(item => {
                item.classList.remove('NavLink__Active')
              })
              subMenuTabContent[0].classList.add('NavLink__Active')
            }
          })

          event.currentTarget.parentElement.classList.add('open')
        }

        return (
          <NavLink key={`NavLink_${id}-${index}`}>
            <button
              className="Navbar__Mobile_Button Menu-item js-Menu-item"
              onClick={e => handleAccordionOpen(e, AccordionTargetClass.MAIN_ACCORDION)}>
              <Typography type="header6" className="Navbar__Link_Name">
                {newGroupName}
              </Typography>
            </button>

            <div className="Navbar__MenuGroup" onMouseLeave={e => mainNavHoverOut(e)}>
              <a href={menuGroupSrc} target={menuGroupTarget} onMouseOver={e => mainNavHover(e)}>
                <Typography type="header6" className="Navbar__Link_Name">
                  {newGroupName}
                </Typography>
              </a>
              <NavMegaMenu
                subLinks={pages}
                numOfGrayColumns={Number(greyColumns)}
                direction={greyColumnAlignment}
                uiPathSrc={uiPathSrc}
                navLinkId={id}
                navIndex={index}
                menuStyle={menuStyle}
                location={location}
                parentBottomText={bottomText}
                parentBottomTextCta={bottomTextCta}
                hideMegamenuFirstColumn={hideMegamenuFirstColumn}
                leftMarginColor={leftMarginColor}
                rightMarginColor={rightMarginColor}
                parentLink={parentLink}
                newGroupName={newGroupName}
              />
            </div>
          </NavLink>
        )
      }
    )}
  </NavLinksWrapper>
)

const NavMegaMenu: FC<MegaMenuProps> = ({
  subLinks,
  numOfGrayColumns,
  direction,
  uiPathSrc,
  navLinkId,
  navIndex,
  menuStyle,
  location,
  parentBottomText,
  parentBottomTextCta,
  hideMegamenuFirstColumn,
  leftMarginColor,
  rightMarginColor,
  parentLink,
  newGroupName,
}) => {
  let mainNavBannerData: any = null
  if (subLinks[subLinks.length - 1]?.__typename === ModuleNames.MAIN_NAV_BANNER) {
    mainNavBannerData = subLinks[subLinks.length - 1]
  }

  const [ActiveState, setActiveState] = useState(0)
  const toggleTab = (index: number) => {
    setActiveState(index)
  }
  const getActiveClass = (index: number, className: string) =>
    ActiveState === index ? className : ''

  const renderListData = (
    pages: any,
    moduleNames: string,
    mainNavBannerData: any,
    menulevel: string
  ) => {
    return (
      <>
        {pages?.map(
          (
            {
              groupNameColor,
              menuGroupDescription,
              newGroupName,
              bottomText,
              pages,
              id,
              highlightedLinks,
              ctaText,
              icon,
              highlightText,
              url,
              subtitle,
              bgColor,
              twoColumn,
              parentLink,
              __typename,
            }: any,
            index: number
          ) => {
            if (__typename === moduleNames) {
              const hasIconLink = pages && pages[0]?.__typename === ModuleNames.TOPIC_ICON_LINK
              highlightedLinks?.forEach((highlightedLink: any) => {
                highlightedLink.isHighlightedLink = true
              })

              const mLevelHover = () => {
                menulevel === 'mlevel'
                  ? __typename === ModuleNames.MENU_GROUP
                    ? toggleTab(index)
                    : ''
                  : ''
              }

              const mLevelHoverClass =
                menulevel === 'mlevel'
                  ? `${
                      __typename === ModuleNames.MENU_GROUP
                        ? `sub-mgrouptab ${getActiveClass(index, 'is-active')}`
                        : ''
                    }`
                  : ''

              return (
                <MegaMenuSection
                  key={`MegaNavSection_${id}-${index}`}
                  $textColor={groupNameColor}
                  $bgColor={bgColor}
                  $twoColumn={twoColumn}
                  $numOfGrayColumns={numOfGrayColumns}
                  $position={index + 1}
                  $direction={direction}
                  $maxLength={subLinks?.length}
                  $doesContentExist={menuGroupDescription || newGroupName}
                  $hasIconLink={hasIconLink}
                  $doesMainNavBannerExist={!!mainNavBannerData}
                  onMouseOver={mLevelHover}
                  className={`${__typename} ${mLevelHoverClass}`}>
                  {displaySublistComponent(
                    index,
                    pages,
                    newGroupName,
                    highlightedLinks,
                    menuGroupDescription,
                    __typename,
                    uiPathSrc,
                    navLinkId,
                    icon,
                    url,
                    ctaText,
                    highlightText,
                    mainNavBannerData,
                    twoColumn,
                    parentLink,
                    menulevel
                  )}
                </MegaMenuSection>
              )
            }
          }
        )}
      </>
    )
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const slevelArray = document.querySelectorAll('.slevel-group')
      const mlevelArray = document.querySelectorAll('.mlevel-group')

      slevelArray.forEach((item: any) => {
        const element = item.querySelector(
          '.Navbar__MegaMenuColumn-Right .Left-Section ul > li:first-child'
        )
        const backgroundColor = element ? window.getComputedStyle(element).backgroundColor : ''
        const children = item.querySelectorAll('.Navbar__Sub_Accordion_All_Link')
        children.forEach((child: HTMLElement) => {
          child.style.backgroundColor = backgroundColor
        })
      })

      mlevelArray.forEach((item: any) => {
        const element = item.querySelector('.sub-mgrouptab.is-active')
        const backgroundColor = element ? window.getComputedStyle(element).backgroundColor : ''
        const children = item.querySelectorAll('.Navbar__Sub_Accordion_All_Link')
        children.forEach((child: HTMLElement) => {
          child.style.backgroundColor = backgroundColor
        })
      })
    }
  }, [])

  const firstLevelInternalLinkSrc =
    parentLink?.internalLink?.slug && uiPathSrc + parentLink.internalLink.slug
  const firstLevelMenuGroupSrc = parentLink?.externalUrl || firstLevelInternalLinkSrc || '#'
  const firstLevelMenuGroupTarget = isExternalURL(firstLevelMenuGroupSrc) ? '_blank' : '_self'
  const firstLevelNewGroupName = newGroupName
  return (
    <MegaMenu
      className="Navbar__MegaMenu"
      id={`MegaMenu_${navLinkId}`}
      $direction={direction}
      $numOfGrayColumns={numOfGrayColumns}
      $maxLength={subLinks?.length}
      $doesMainNavBannerExist={!!mainNavBannerData}
      $leftMarginColor={leftMarginColor}
      $rightMarginColor={rightMarginColor}
      onMouseLeave={e => mainNavHoverOut(e)}>
      <div className="MegaMenu__Side Left"></div>
      {menuStyle === MENU_STYLES.TABS &&
      (location.includes('custom header') ||
        location.includes('New Header Testing') ||
        location.includes('V3 Header German') ||
        location.includes('V3 Header Chinese') ||
        location.includes('V3 Header Trad Chinese') ||
        location.includes('V3 Header French') ||
        location.includes('V3 Header Korean') ||
        location.includes('V3 Header Portuguese') ||
        location.includes('V3 Header Japanese')) ? (
        <MegaMenuContainer className="Navbar__MegaMenuColumn mlevel-group">
          <li className="Navbar__MegaMenuColumn-Left">
            <ul>
              {renderListData(subLinks, ModuleNames.MENU_GROUP, mainNavBannerData, 'mlevel')}
              {renderListData(subLinks, ModuleNames.TOPIC_ICON_LINK, '', '')}
            </ul>
          </li>
          <li className="Navbar__MegaMenuColumn-Right">
            {subLinks?.map(
              (
                {
                  newGroupName,
                  menuGroupDescription,
                  pages,
                  bottomText,
                  bottomTextCta,
                  parentLink,
                }: any,
                index: number
              ) => {
                let mainNavBannerData: any = null
                const internalLinkSrc =
                  parentLink?.internalLink?.slug && uiPathSrc + parentLink.internalLink.slug
                const menuGroupSrc = parentLink?.externalUrl || internalLinkSrc || '#'
                const menuGroupTarget = isExternalURL(menuGroupSrc) ? '_blank' : '_self'

                if (pages && pages[pages.length - 1]?.__typename === ModuleNames.MAIN_NAV_BANNER) {
                  mainNavBannerData = pages[pages.length - 1]
                }

                return (
                  <React.Fragment key={`megamenu_${index}`}>
                    {newGroupName && (
                      <div
                        className={`${newGroupName} Navbar__Sub_Accordion ${getActiveClass(
                          index,
                          'NavLink__Active'
                        )}`}>
                        <div
                          className={`Navbar__Sub_Accordion_Item ${getActiveClass(
                            index,
                            'is-active'
                          )}`}
                          onClick={e => handleAccordionOpen(e, AccordionTargetClass.SUB_ACCORDION)}>
                          <Typography type="label" className="Navbar__Sub_Accordion_Name">
                            {newGroupName}
                          </Typography>
                        </div>

                        <div className="Navbar__Sub_Accordion_MenuGroup">
                          <div className="Left-Section">
                            <Typography type="label" className="Navbar__Sub_Accordion_Name">
                              <a
                                href={menuGroupSrc}
                                target={menuGroupTarget}
                                className="Navbar__Section_Content_Link">
                                {newGroupName}
                              </a>
                            </Typography>
                            <Typography type="micro" className="Navbar__Sub_Accordion_Desc">
                              {formatInlineCode(menuGroupDescription)}
                            </Typography>

                            <ul
                              className={`menu-tab-content ${bottomText ? 'has-bottom-text' : ''}`}>
                              {renderListData(pages, ModuleNames.MENU_GROUP, mainNavBannerData, '')}
                            </ul>

                            {bottomText && (
                              <div className="Navbar__Sub_Accordion_All_Link">
                                <Button
                                  icon={true}
                                  type={'text-link'}
                                  theme={'dark'}
                                  href={
                                    bottomTextCta?.internalLink
                                      ? internalLinkHandler(bottomTextCta?.internalLink)
                                      : bottomTextCta?.externalUrl || ''
                                  }
                                  text={formatInlineCode(bottomText)}
                                  size="medium"
                                  arrowOrientation="right"
                                />
                              </div>
                            )}
                          </div>

                          <div className="Right-Section">
                            <ul className="menu-tab-content">
                              {renderListData(
                                pages,
                                ModuleNames.MAIN_NAV_BANNER,
                                mainNavBannerData,
                                ''
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                )
              }
            )}
          </li>
        </MegaMenuContainer>
      ) : (
        <MegaMenuContainer className="Navbar__MegaMenuColumn slevel-group">
          {!hideMegamenuFirstColumn ? (
            <li className="Navbar__MegaMenuColumn-Left">
              <a
                href={firstLevelMenuGroupSrc}
                target={firstLevelMenuGroupTarget}
                className="FirstLevel__MenuGroup-Link Mobile">
                <Typography type="label">{firstLevelNewGroupName}</Typography>
              </a>
              <ul>
                {subLinks.map((item: any, index: number) => {
                  if (index === 0) {
                    return renderListData([item], ModuleNames.MENU_GROUP, '', '')
                  }
                })}
              </ul>
            </li>
          ) : (
            <li className="Navbar__MegaMenuColumn-Left Mobile">
              <a
                href={firstLevelMenuGroupSrc}
                target={firstLevelMenuGroupTarget}
                className="FirstLevel__MenuGroup-Link">
                <Typography type="label">{firstLevelNewGroupName}</Typography>
              </a>
            </li>
          )}

          <li
            className={`Navbar__MegaMenuColumn-Right ${
              hideMegamenuFirstColumn ? 'Hide__FirstColumn' : ''
            }`}>
            <div className="Left-Section">
              <ul>
                {subLinks.map((item: any, index: number) => {
                  if (hideMegamenuFirstColumn) {
                    if (item.__typename === ModuleNames.MENU_GROUP) {
                      return renderListData([item], ModuleNames.MENU_GROUP, '', '')
                    }
                  } else {
                    if (index !== 0) {
                      if (item.__typename === ModuleNames.MENU_GROUP) {
                        return renderListData([item], ModuleNames.MENU_GROUP, '', '')
                      }
                    }
                  }
                })}
              </ul>
              {parentBottomText && (
                <div className="Navbar__Sub_Accordion_All_Link">
                  <Button
                    icon={true}
                    type={'text-link'}
                    theme={'dark'}
                    href={
                      parentBottomTextCta?.internalLink
                        ? internalLinkHandler(parentBottomTextCta?.internalLink)
                        : parentBottomTextCta?.externalUrl || ''
                    }
                    text={formatInlineCode(parentBottomText)}
                    size="medium"
                    arrowOrientation="right"
                  />
                </div>
              )}
            </div>
            <div className="Right-Section">
              <ul>
                {subLinks.map((item: any, index: number) => {
                  if (index !== 0) {
                    if (item.__typename === ModuleNames.MAIN_NAV_BANNER) {
                      return renderListData(
                        [item],
                        ModuleNames.MAIN_NAV_BANNER,
                        mainNavBannerData,
                        ''
                      )
                    }
                  }
                })}
              </ul>
            </div>
          </li>
        </MegaMenuContainer>
      )}
      <div className="MegaMenu__Side Right"></div>
    </MegaMenu>
  )
}

const SubList = (subList: (TopicCTA | MenuGroup)[], uiPathSrc: string) =>
  subList?.map(
    (
      {
        externalUrl,
        id,
        internalLink,
        text,
        ctaText,
        icon,
        url,
        __typename,
        description,
        isHighlightedLink,
        highlightText,
      }: any,
      index: number
    ) => {
      const internalLinkSrc = internalLink?.slug && uiPathSrc + internalLink.slug
      const subListUrl = externalUrl || internalLinkSrc || '#'
      const subListTarget = isExternalURL(subListUrl) ? '_blank' : '_self'
      const iconLinkTarget = isExternalURL(url) ? '_blank' : '_self'
      const iconUrl = icon?.image?.file?.url || null

      const iconImg = (
        <WrapperImage
          src={ImgUtil.getResizedImageUrl(icon?.image?.file?.url, '')}
          width={18}
          alt={icon?.altText}
        />
      )

      const iconLabel = (
        <Typography type="label">
          {ctaText}
          {highlightText ? <span>{highlightText}</span> : ''}
        </Typography>
      )
      const iconDescription = (
        <Typography type="micro" className="Navbar__IconLink_Caption">
          {description}
        </Typography>
      )

      return (
        <SubListItem
          key={`SubList_${id}-${index}`}
          $subListLength={subList.length}
          $isThereDescription={description}>
          {__typename === ModuleNames.TOPIC_ICON_LINK ? (
            <a href={url} target={iconLinkTarget} className="Navbar__SubList_IconLink">
              <div className="Navbar__IconLink_Header">
                {iconUrl && iconImg}
                {ctaText && iconLabel}
              </div>
              {description && iconDescription}
            </a>
          ) : isHighlightedLink ? (
            <Button
              className="Navbar__HighlightedLink"
              icon={true}
              type={'text-link'}
              theme={'light'}
              href={subListUrl}
              text={text}
              size="medium"
              arrowOrientation="right"
            />
          ) : text ? (
            <a
              key={`SubList_${id}-${index}`}
              href={subListUrl}
              target={subListTarget}
              className="Navbar__SubList_Cta">
              <Typography type="label">{text}</Typography>
            </a>
          ) : (
            ''
          )}
        </SubListItem>
      )
    }
  )

const displaySublistComponent = (
  index: number,
  pages: any,
  newGroupName: string,
  highlightedLinks: any,
  menuGroupDescription: string,
  __typename: string,
  uiPathSrc: string,
  navLinkId: any,
  icon: any,
  url: string,
  ctaText: string,
  highlightText: string,
  mainNavBannerData: any,
  twoColumn: boolean,
  parentLink: any,
  mlevel: string
) => {
  const pagesWithHighlightedLinks = pages ? pages.concat(highlightedLinks || []) : []
  const mid = Math.ceil(pagesWithHighlightedLinks.length / 2) || 0
  const leftPages = pagesWithHighlightedLinks.slice(0, mid)
  const rightPages = pagesWithHighlightedLinks.slice(mid)
  let mgTwoColumn = twoColumn ? 'Two__Column' : ''

  const internalLinkSrc = parentLink?.internalLink?.slug && uiPathSrc + parentLink.internalLink.slug
  const menuGroupSrc = parentLink?.externalUrl || internalLinkSrc
  const menuGroupTarget = isExternalURL(menuGroupSrc) ? '_blank' : '_self'

  const sectionName = (
    <Typography type="body" className="Navbar__Section_Name">
      {newGroupName}
    </Typography>
  )

  const sectionDescription = (
    <Typography type="micro" className="Navbar__Section_Description">
      {formatInlineCode(menuGroupDescription)}
    </Typography>
  )

  switch (__typename) {
    case ModuleNames.MENU_GROUP:
      return (
        <div
          key={`MenuGroup_${navLinkId}-${index}`}
          className={`Navbar__Section_Container ${mgTwoColumn}`}>
          {(newGroupName || menuGroupDescription) && (
            <div className="Navbar__Section_Content">
              {mlevel === 'mlevel' && menuGroupSrc && (
                <a
                  href={menuGroupSrc}
                  target={menuGroupTarget}
                  className="Navbar__Section_Content_Link"></a>
              )}
              {newGroupName && sectionName}
              {menuGroupDescription && sectionDescription}
            </div>
          )}

          {(leftPages.length || rightPages.length) && (
            <>
              <div className="SubList__Left">{SubList(leftPages, uiPathSrc)}</div>
              <div className="SubList__Right">{SubList(rightPages, uiPathSrc)}</div>
            </>
          )}
        </div>
      )
    case ModuleNames.TOPIC_ICON_LINK:
      return (
        <div className="Navbar__IconLink_Header">
          {icon && (
            <WrapperImage
              src={ImgUtil.getResizedImageUrl(icon?.image?.file?.url, '')}
              width={18}
              alt={icon?.altText}
            />
          )}

          <Typography type="label">
            <a
              href={url}
              target={isExternalURL(url) ? '_blank' : '_self'}
              className="Navbar__SubList_IconLink">
              {ctaText} {highlightText && <span>{highlightText}</span>}
            </a>
          </Typography>
        </div>
      )
    case ModuleNames.MAIN_NAV_BANNER:
      return (
        <MainNavBanner
          key={`MainNavBanner_${navLinkId}-${index}`}
          mainNavBannerData={mainNavBannerData}
          navType="New Nav"
        />
      )

    default:
      return null
  }
}
